import { FC, useMemo } from 'react';
import { CheckLineIcon } from '@svg/CheckLineIcon';
import { CheckCheckLineIcon } from '@svg/CheckCheckLineIcon';
import { CircleAlertLineIcon } from '@svg/CircleAlertLineIcon';
import { LockLineIcon } from '@svg/LockLineIcon';
import { MessageSquareDashedLineIcon } from '@svg/MessageSquareDashedLineIcon';
import { MessageSquarePlusLineIcon } from '@svg/MessageSquarePlusLineIcon';
import { SearchLineIcon } from '@svg/SearchLineIcon';
import { XLineIcon } from '@svg/XLineIcon';
import { useLyftSocket } from '@src/hooks';
import { useAppSelector } from '@store/hooks';
import type {
  ConversationCounts,
  ConversationFilter,
} from '@store/rtk/driverChat';
import { selectDriverChat } from '@store/slices/driverChat/driverChatSlice';
import { resolveChatStatus } from '@component/DriverChat/hooks/useLiveChatStatus';
import { MemberAvatar } from '@component/DriverChat/MemberAvatar/MemberAvatar';
import {
  formatChatShortTime,
  getMemberFullName,
  getSegmentReference,
  getStatusLabel,
} from '@component/DriverChat/helpers';
import {
  DriverChatSeed,
  DriverConversation,
} from '@component/DriverChat/types';

type ConversationListProps = {
  conversations: DriverConversation[];
  counts?: ConversationCounts;
  filter: ConversationFilter;
  onFilterChange: (filter: ConversationFilter) => void;
  search: string;
  onSearchChange: (search: string) => void;
  appliedSearch: string;
  isLoading: boolean;
  isError: boolean;
  onReload: () => void;
  activeConversationId: number | null;
  onOpen: (conversationId: number) => void;
  /** Accepted rides with a driver that have no conversation yet. */
  startable: DriverChatSeed[];
  onStart: (seed: DriverChatSeed) => void;
  isStarting: boolean;
  onClose: () => void;
  titleId: string;
};

const TABS: {
  filter: ConversationFilter;
  label: string;
  count: keyof ConversationCounts;
}[] = [
  { filter: 'MINE', label: 'My Conversations', count: 'mine' },
  { filter: 'ACTIVE', label: 'Active rides', count: 'active' },
  { filter: 'TODAY', label: 'All today', count: 'today' },
];

export const ConversationList: FC<ConversationListProps> = ({
  conversations,
  counts,
  filter,
  onFilterChange,
  search,
  onSearchChange,
  appliedSearch,
  isLoading,
  isError,
  onReload,
  activeConversationId,
  onOpen,
  startable,
  onStart,
  isStarting,
  onClose,
  titleId,
}) => {
  const { socketStatus } = useLyftSocket();
  const { segmentDetails } = useAppSelector(selectDriverChat);

  const matchesTerm = (seed: DriverChatSeed, term: string) =>
    !term ||
    `${getMemberFullName(seed)} ${seed.confirmationCode ?? ''} ${getSegmentReference(seed)} ${seed.driverFirstName} ${seed.segmentId}`
      .toLowerCase()
      .includes(term);

  const isOpen = (c: DriverConversation) =>
    resolveChatStatus(
      socketStatus,
      c.segmentId,
      c.statusId,
      c.conversationStatus
    ).isChatOpen;

  const { active, closed } = useMemo(
    () => ({
      active: conversations.filter(isOpen),
      closed: conversations.filter((c) => !isOpen(c)),
    }),
    [conversations, socketStatus]
  );

  const unreadTotal = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  const renderPreview = (c: DriverConversation, isClosed: boolean) => {
    if (isClosed) {
      return (
        <>
          <LockLineIcon
            width={13}
            height={13}
            strokeWidth={1.75}
            aria-hidden="true"
          />
          Conversation closed
        </>
      );
    }
    const last = c.lastMessage;
    if (!last) return 'No messages yet';
    if (last.sender !== 'DISPATCHER') return last.text;
    const ReceiptIcon =
      last.status === 'read' ? CheckCheckLineIcon : CheckLineIcon;
    return (
      <>
        <ReceiptIcon
          width={14}
          height={14}
          strokeWidth={2}
          aria-hidden="true"
        />
        {last.text}
      </>
    );
  };

  const renderStartable = (seed: DriverChatSeed) => (
    <button
      key={`start-${seed.segmentId}`}
      type="button"
      className="dc-conversation"
      disabled={isStarting}
      onClick={() => onStart(seed)}
      data-test-id={`driver-chat-start-${seed.segmentId}`}
    >
      <MemberAvatar
        name={getMemberFullName(seed) || seed.driverFirstName}
        image={seed.memberImage}
        size="sm"
      />
      <span className="dc-conversation__main">
        <span className="dc-conversation__top">
          <b>{getMemberFullName(seed) || seed.driverFirstName || 'Driver'}</b>
          <span className="dc-conversation__segment">
            {getSegmentReference(seed)}
          </span>
        </span>
        <span className="dc-conversation__bottom">
          <span className="dc-conversation__preview">
            <MessageSquarePlusLineIcon
              width={14}
              height={14}
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Start conversation · {getStatusLabel(seed.statusId)}
          </span>
        </span>
      </span>
    </button>
  );

  const renderRow = (c: DriverConversation) => {
    const isClosed = !isOpen(c);
    return (
      <button
        key={c.conversationId}
        type="button"
        className={`dc-conversation ${c.unreadCount ? 'is-unread' : ''}`}
        aria-current={c.conversationId === activeConversationId}
        onClick={() => onOpen(c.conversationId)}
        data-test-id={`driver-chat-conversation-${c.segmentId}`}
      >
        <MemberAvatar
          name={getMemberFullName(c) || c.driverFirstName}
          image={c.memberImage}
          size="sm"
        />
        <span className="dc-conversation__main">
          <span className="dc-conversation__top">
            <b>{getMemberFullName(c)}</b>
            <span className="dc-conversation__segment">
              {getSegmentReference(c)}
            </span>
            <time>{formatChatShortTime(c.lastMessage?.createdAt)}</time>
          </span>
          <span className="dc-conversation__bottom">
            <span className="dc-conversation__preview">
              {renderPreview(c, isClosed)}
            </span>
            {c.unreadCount > 0 && (
              <span className="dc-badge" aria-label={`${c.unreadCount} unread`}>
                {c.unreadCount}
              </span>
            )}
          </span>
        </span>
      </button>
    );
  };

  const renderList = () => {
    if (isLoading) {
      return (
        <div
          className="dc-conversations__skeleton"
          aria-label="Loading conversations"
        >
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="dc-skeleton" />
          ))}
        </div>
      );
    }
    if (isError) {
      return (
        <div className="dc-empty">
          <span className="dc-empty__icon">
            <CircleAlertLineIcon
              width={26}
              height={26}
              strokeWidth={1.6}
              aria-hidden="true"
            />
          </span>
          <b>Conversations couldn't load</b>
          <button type="button" className="dc-link" onClick={onReload}>
            Try again
          </button>
        </div>
      );
    }
    const visibleClosed = closed;
    const term = appliedSearch.toLowerCase();
    const visibleStartable = startable
      .map((seed) => ({
        ...seed,
        ...segmentDetails?.[seed.segmentId],
      }))
      .filter((seed) => matchesTerm(seed, term));
    if (!active.length && !visibleClosed.length && !visibleStartable.length) {
      return (
        <div className="dc-empty">
          <span className="dc-empty__icon">
            <MessageSquareDashedLineIcon
              width={26}
              height={26}
              strokeWidth={1.6}
              aria-hidden="true"
            />
          </span>
          <b>{appliedSearch ? 'No matches' : 'No conversations'}</b>
          <span>
            {appliedSearch
              ? 'Try a different member name or confirmation code.'
              : 'Use “Message driver” on an active ride to start one.'}
          </span>
        </div>
      );
    }
    return (
      <>
        {visibleStartable.length > 0 && (
          <>
            <div className="dc-conversations__heading">
              Start a conversation
            </div>
            {visibleStartable.map(renderStartable)}
            {active.length > 0 && (
              <div className="dc-conversations__heading">Active</div>
            )}
          </>
        )}
        {active.map(renderRow)}
        {visibleClosed.length > 0 && (
          <>
            <div className="dc-conversations__heading">Closed</div>
            {visibleClosed.map(renderRow)}
          </>
        )}
      </>
    );
  };

  return (
    <div className="dc-conversations">
      <div className="dc-conversations__header">
        <h2 id={titleId}>
          Messages
          {unreadTotal > 0 && <small>{unreadTotal} unread</small>}
        </h2>
        <button
          type="button"
          className="dc-icon-button"
          aria-label="Close messages"
          onClick={onClose}
        >
          <XLineIcon
            width={18}
            height={18}
            strokeWidth={1.75}
            aria-hidden="true"
          />
        </button>
      </div>
      <div className="dc-conversations__search">
        <SearchLineIcon
          width={16}
          height={16}
          strokeWidth={1.75}
          aria-hidden="true"
        />
        <label className="dc-sr-only" htmlFor="dc-conversation-search">
          Search conversations
        </label>
        <input
          id="dc-conversation-search"
          type="search"
          autoComplete="off"
          placeholder="Member Name or Confirmation Code"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>
      <div
        className="dc-conversations__filters"
        role="group"
        aria-label="Filter conversations"
      >
        {TABS.map((tab) => (
          <button
            key={tab.filter}
            type="button"
            aria-pressed={filter === tab.filter}
            onClick={() => onFilterChange(tab.filter)}
          >
            {tab.label} <span>{counts?.[tab.count] ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="dc-conversations__list">{renderList()}</div>
    </div>
  );
};
